import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  List,
  ListProps,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  ShowButton,
  TextField,
  TopToolbar,
  Translate,
  useGetList,
  useTranslate,
} from 'react-admin'

import { Tooltip } from '@mantine/core'
import { useMemo } from 'react'
import { API } from '../../helpers/Api.helper'
import { ProductsHelper } from '../../helpers/Products.helper'
import { useUserStore } from '../../stores/useUserStore'
import { ProductType } from '../../types/ProductType'
import { Forbidden } from '../Forbidden'
import { ListHelper } from './../../helpers/List.helper'

type ProductListActionsProps = {
  isAuthorised: boolean
  translate: Translate
}
const ProductListActions = ({ isAuthorised, translate }: ProductListActionsProps): JSX.Element => (
  <TopToolbar>
    <Tooltip
      label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
        'pages.users.permissionsList.products.edit',
      )}`}
      hidden={isAuthorised}
    >
      <span>
        <CreateButton disabled={!isAuthorised} />
      </span>
    </Tooltip>
  </TopToolbar>
)

export const ProductList = (props: ListProps): JSX.Element => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const { data } = useGetList<ProductType>('productTypes', {
    filter: {},
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  })

  const productTypes = useMemo(
    () => ProductsHelper.formatProductTypes(data ?? [], translate),
    [data, translate],
  )

  if (!userStore.isAuthorised('products.read')) {
    return <Forbidden permission="products.read" />
  }

  return (
    <List
      {...props}
      actions={
        <ProductListActions
          isAuthorised={userStore.isAuthorised('products.edit')}
          translate={translate}
        />
      }
      exporter={false}
      perPage={API.DEFAULT_PAGINATION.perPage}
      sort={{ field: 'name', order: 'DESC' }}
      filters={[
        <SearchInput key="search" source="q" alwaysOn />,
        <AutocompleteInput
          key="productType"
          source="productTypeId"
          label="_generics.productType"
          choices={productTypes}
          groupBy={option => option.group}
          alwaysOn
        />,
        <ReferenceInput
          key="beneficiaryId"
          source="beneficiaryId"
          reference="beneficiaries"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="name" label={translate('_generics.beneficiary')} />
        </ReferenceInput>,
      ]}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="name" label={translate('_generics.name')} />
        <ReferenceField
          source="productTypeId"
          reference="productTypes"
          label={translate('pages.products.productType')}
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          source="beneficiaryId"
          reference="beneficiaries"
          label={translate('pages.products.beneficiary')}
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <ShowButton />
      </Datagrid>
    </List>
  )
}
